import { Component, computed, effect, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { SucursalApi, ZonaService, ZonaCentroApi, ZonaEntregaApi } from '../../core/services/zona.service';
import { DeliveryServicioService, DeliveryServicioApi, VarianteServicioApi } from '../../core/services/delivery-servicio.service';
import { EmpresaService } from '../../core/services/empresa.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

const DEFAULT_COLOR = '#d61f45';
const SIN_TIENDA = '__sin_tienda__';
const DEFAULT_RADIO = 1000;

@Component({
    selector: 'app-admin-zonas',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, GoogleMapsModule, UiLoadingComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Zonas de entrega</h2>
      <p class="text-sm text-gray-400 mt-1">Elige una tienda y configura las zonas que cubre. El precio del delivery sale del tarifario del servicio de la zona.</p>
    </div>

    <div *ngIf="tallerSinPunto() && !sucursalSel()" class="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex flex-wrap items-center gap-3">
      <p class="text-sm font-bold text-gray-800 flex-1 min-w-52">El taller aún no tiene su punto exacto en el mapa.</p>
      <a routerLink="/admin/taller" class="px-4 py-2 rounded-2xl text-white text-xs font-bold cursor-pointer" style="background-color: var(--bg-main);">Configurar taller</a>
    </div>

    <app-ui-loading *ngIf="cargando()"></app-ui-loading>

    <!-- PASO 1: ELEGIR TIENDA -->
    <div *ngIf="!cargando() && !sucursalSel()" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <button *ngFor="let s of sucursales()" type="button" (click)="elegirSucursal(s)"
        class="text-left bg-white rounded-[2rem] border border-rose-100 shadow-sm p-5 flex flex-col gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
        <div class="flex items-center justify-between gap-2">
          <p class="text-base font-black text-gray-800">{{ s.nombre }}</p>
          <span class="text-[10px] font-bold px-2 py-0.5 rounded-full"
            [class.bg-emerald-50]="zonasDe(s.uuid).length > 0" [class.text-emerald-600]="zonasDe(s.uuid).length > 0"
            [class.bg-gray-100]="zonasDe(s.uuid).length === 0" [class.text-gray-500]="zonasDe(s.uuid).length === 0">
            {{ zonasDe(s.uuid).length }} zona(s)
          </span>
        </div>
        <p class="text-xs text-gray-500">{{ s.direccion || 'Dirección no configurada' }}</p>
        <span class="mt-1 inline-flex items-center gap-1 text-xs font-bold text-rose-500">Configurar zonas
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
        </span>
      </button>
      <button *ngIf="zonasDe(SIN_TIENDA).length > 0" type="button" (click)="elegirSucursal(sinTienda())"
        class="text-left bg-amber-50 rounded-[2rem] border border-amber-200 shadow-sm p-5 flex flex-col gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
        <div class="flex items-center justify-between gap-2">
          <p class="text-base font-black text-gray-800">Sin tienda asignada</p>
          <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{{ zonasDe(SIN_TIENDA).length }} zona(s)</span>
        </div>
        <p class="text-xs text-gray-500">Zonas antiguas sin tienda. Edítalas para asignarles una.</p>
        <span class="mt-1 inline-flex items-center gap-1 text-xs font-bold text-amber-700">Revisar zonas</span>
      </button>
      <p *ngIf="sucursales().length === 0 && zonasDe(SIN_TIENDA).length === 0" class="sm:col-span-2 lg:col-span-3 bg-white rounded-[2rem] border border-rose-100 px-5 py-8 text-center text-sm text-gray-400">
        No hay tiendas configuradas en inventa. Créalas en InVenta y aparecerán aquí.
      </p>
    </div>

    <!-- PASO 2: ZONAS DE LA TIENDA -->
    <div *ngIf="!cargando() && sucursalSel() as s">
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <button type="button" (click)="volver()" class="w-10 h-10 rounded-2xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer flex items-center justify-center">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div class="flex-1 min-w-52">
          <h3 class="text-lg font-black text-gray-800">{{ s.nombre }}</h3>
          <p class="text-xs text-gray-400">{{ s.direccion || 'Dirección no configurada' }}</p>
        </div>
        <button type="button" (click)="nuevaZona()" class="px-5 py-3 rounded-2xl text-white text-sm font-bold cursor-pointer" style="background-color: var(--bg-main);">Nueva zona</button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="lg:col-span-2 bg-white rounded-[2rem] border border-rose-100 shadow-sm overflow-hidden">
          <google-map #mapa width="100%" height="480px" [center]="center()" [zoom]="13" [options]="mapOptions" (mapInitialized)="onMapReady()"></google-map>
        </div>

        <div class="flex flex-col gap-4">
          <div *ngIf="formVisible()" class="bg-white rounded-[2rem] border border-rose-100 shadow-sm p-5 flex flex-col gap-3">
            <p class="text-sm font-black text-gray-800">{{ seleccionada() ? 'Editar zona' : 'Nueva zona' }}</p>
            <label *ngIf="sucursalSel()?.uuid === SIN_TIENDA" class="block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Asignar a tienda</span>
              <select [(ngModel)]="establecimientoDestino" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
                <option [ngValue]="null">Sin tienda</option>
                <option *ngFor="let s of sucursales()" [ngValue]="s.uuid">{{ s.nombre }}</option>
              </select>
            </label>
            <label class="block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
              <input [(ngModel)]="nombre" placeholder="Ej: Centro, Salcedo…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
            </label>
            <label class="block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Color</span>
              <input [(ngModel)]="color" (ngModelChange)="pintarBorrador()" type="color" class="mt-1 w-full h-11 rounded-2xl border border-gray-200 px-2 cursor-pointer" />
            </label>
            <label class="block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Servicio de delivery (precio)</span>
              <select [ngModel]="deliveryServicioUuid()" (ngModelChange)="deliveryServicioUuid.set($event)" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
                <option [ngValue]="null">Sin servicio</option>
                <option *ngFor="let d of deliveryServicios()" [ngValue]="d.uuid">{{ d.nombre }}</option>
              </select>
            </label>
            <label class="block">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Radio (metros)</span>
              <input [(ngModel)]="radio" (ngModelChange)="pintarBorrador()" type="number" min="100" step="100" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
            </label>
            <p class="text-xs text-gray-400">{{ centroActual() ? 'Centro marcado. Tócalo de nuevo en el mapa para moverlo.' : 'Toca el mapa para marcar el centro del círculo.' }}</p>
            <div class="flex gap-2">
              <button type="button" (click)="guardar()" [disabled]="guardando()" class="flex-1 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">{{ guardando() ? 'Guardando…' : (seleccionada() ? 'Guardar cambios' : 'Crear zona') }}</button>
              <button type="button" (click)="cancelarEdicion()" class="px-4 py-3 rounded-2xl border border-gray-200 text-sm font-bold text-gray-600 cursor-pointer">Cancelar</button>
            </div>
            <ng-container *ngIf="seleccionada()">
              <button *ngIf="confirmarEliminar() !== seleccionada()" type="button" (click)="confirmarEliminar.set(seleccionada())" class="py-2 text-xs font-bold text-red-500 hover:underline cursor-pointer">Eliminar zona</button>
              <div *ngIf="confirmarEliminar() === seleccionada()" class="flex items-center gap-3 text-xs font-bold">
                <span class="text-red-500">¿Eliminar esta zona?</span>
                <button type="button" (click)="eliminar()" class="text-red-500 hover:underline cursor-pointer">Sí, eliminar</button>
                <button type="button" (click)="confirmarEliminar.set(null)" class="text-gray-400 hover:underline cursor-pointer">Cancelar</button>
              </div>
            </ng-container>
          </div>

          <div class="bg-white rounded-[2rem] border border-rose-100 shadow-sm p-5 flex flex-col gap-2">
            <p class="text-sm font-black text-gray-800">Zonas de {{ s.nombre }} ({{ zonasDe(s.uuid).length }})</p>
            <p *ngIf="zonasDe(s.uuid).length === 0" class="text-xs text-gray-400 px-3 py-2">Aún no hay zonas en esta tienda. Pulsa "Nueva zona" y marca el centro en el mapa.</p>
            <button *ngFor="let z of zonasDe(s.uuid)" type="button" (click)="seleccionar(z.uuid)" class="flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-rose-50 cursor-pointer" [class.bg-rose-50]="seleccionada() === z.uuid">
              <span class="w-4 h-4 rounded-full shrink-0" [style.background-color]="z.color"></span>
              <span class="text-sm font-bold text-gray-700 flex-1 truncate">{{ z.nombre }}</span>
              <span class="text-[10px] text-gray-400">{{ z.radioMetros }} m</span>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" [class.bg-emerald-50]="z.deliveryServicioUuid" [class.text-emerald-600]="z.deliveryServicioUuid" [class.bg-amber-50]="!z.deliveryServicioUuid" [class.text-amber-600]="!z.deliveryServicioUuid">
                {{ z.deliveryServicioUuid ? 'Con precio' : 'Sin precio' }}
              </span>
            </button>
          </div>

          <div class="bg-white rounded-[2rem] border border-rose-100 shadow-sm p-5 flex flex-col gap-3">
            <button type="button" (click)="mostrarServicios.set(!mostrarServicios())" class="flex items-center justify-between gap-2 cursor-pointer">
              <span class="text-sm font-black text-gray-800">Servicios de delivery</span>
              <span class="text-xs text-gray-400">{{ mostrarServicios() ? 'Ocultar' : 'Configurar' }}</span>
            </button>
            <p class="text-xs text-gray-400">El servicio se vende como delivery; su tarifario define el precio. Es global para todas las tiendas.</p>
            <ng-container *ngIf="mostrarServicios()">
              <label class="block">
                <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
                <input [(ngModel)]="deliveryNombre" placeholder="Ej: Delivery general" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
              </label>
              <label class="block">
                <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Servicio de inventa</span>
                <input [(ngModel)]="filtroVariante" placeholder="Buscar por SKU o nombre…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
                <select [(ngModel)]="deliveryVarianteId" size="4" class="mt-2 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
                  <option *ngFor="let v of variantesFiltradas()" [value]="v.uuid">{{ v.sku }} · {{ v.nombre }}</option>
                </select>
              </label>
              <div class="flex gap-2">
                <button type="button" (click)="guardarDelivery()" [disabled]="guardandoDelivery() || !deliveryNombre().trim() || !deliveryVarianteId()" class="flex-1 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">{{ deliveryEditUuid() ? 'Guardar cambios' : 'Agregar servicio' }}</button>
                <button *ngIf="deliveryEditUuid()" type="button" (click)="cancelarDelivery()" class="px-4 py-3 rounded-2xl border border-gray-200 text-sm font-bold text-gray-600 cursor-pointer">Cancelar</button>
              </div>
              <p *ngIf="deliveryServicios().length === 0" class="text-xs text-gray-400">Aún no hay servicios de delivery.</p>
              <button *ngFor="let d of deliveryServicios()" type="button" (click)="editarDelivery(d)" class="flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-rose-50 cursor-pointer">
                <span class="text-sm font-bold text-gray-700 flex-1 truncate">{{ d.nombre }}</span>
                <span class="text-xs text-gray-400">{{ d.sku }}</span>
              </button>
              <ng-container *ngIf="deliveryEditUuid()">
                <button *ngIf="confirmarEliminarDelivery() !== deliveryEditUuid()" type="button" (click)="confirmarEliminarDelivery.set(deliveryEditUuid())" class="py-2 text-xs font-bold text-red-500 hover:underline cursor-pointer">Eliminar servicio</button>
                <div *ngIf="confirmarEliminarDelivery() === deliveryEditUuid()" class="flex items-center gap-3 text-xs font-bold">
                  <span class="text-red-500">¿Eliminar este servicio?</span>
                  <button type="button" (click)="eliminarDelivery()" class="text-red-500 hover:underline cursor-pointer">Sí, eliminar</button>
                  <button type="button" (click)="confirmarEliminarDelivery.set(null)" class="text-gray-400 hover:underline cursor-pointer">Cancelar</button>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
    `
})
export class AdminZonasComponent implements OnInit, OnDestroy {
    SIN_TIENDA = SIN_TIENDA;
    mapa = viewChild<GoogleMap>('mapa');

    private zonasService = inject(ZonaService);
    private deliveryService = inject(DeliveryServicioService);
    private empresa = inject(EmpresaService);
    private alerts = inject(AlertService);

    center = signal<google.maps.LatLngLiteral>({ lat: -15.8402, lng: -70.0219 });
    mapOptions: google.maps.MapOptions = { mapTypeControl: false, streetViewControl: false, fullscreenControl: false };
    cargando = signal(true);
    sucursales = signal<SucursalApi[]>([]);
    sucursalSel = signal<SucursalApi | null>(null);
    zonas = signal<ZonaEntregaApi[]>([]);
    seleccionada = signal<string | null>(null);
    formVisible = signal(false);
    nombre = signal('');
    color = signal(DEFAULT_COLOR);
    radio = signal<number>(DEFAULT_RADIO);
    centroActual = signal<ZonaCentroApi | null>(null);
    guardando = signal(false);
    confirmarEliminar = signal<string | null>(null);
    tallerSinPunto = signal(false);
    deliveryServicios = signal<DeliveryServicioApi[]>([]);
    variantesServicio = signal<VarianteServicioApi[]>([]);
    deliveryNombre = signal('');
    deliveryVarianteId = signal('');
    deliveryEditUuid = signal<string | null>(null);
    guardandoDelivery = signal(false);
    deliveryServicioUuid = signal<string | null>(null);
    mostrarServicios = signal(false);
    establecimientoDestino = signal<string | null>(null);
    filtroVariante = signal('');
    confirmarEliminarDelivery = signal<string | null>(null);

    variantesFiltradas = computed(() => {
        const term = this.filtroVariante().trim().toLowerCase();
        const rows = this.variantesServicio();
        return term ? rows.filter((v) => `${v.sku} ${v.nombre}`.toLowerCase().includes(term)) : rows;
    });

    private circulos = new Map<string, google.maps.Circle>();
    private pines = new Map<string, google.maps.Marker>();
    private borrador: google.maps.Circle | null = null;
    private clickListener: google.maps.MapsEventListener | null = null;

    constructor() {
        effect(() => {
            const map = this.mapa()?.googleMap;
            if (map) {
                this.pintarBorrador();
            }
        });
    }

    ngOnInit(): void {
        this.empresa.getTaller().subscribe({
            next: (t) => {
                if (t?.latitud != null && t?.longitud != null) {
                    this.center.set({ lat: t.latitud, lng: t.longitud });
                    this.tallerSinPunto.set(false);
                } else {
                    this.tallerSinPunto.set(true);
                }
            },
            error: () => this.tallerSinPunto.set(true)
        });
        this.zonasService.listSucursales().subscribe({
            next: (rows) => this.sucursales.set(rows),
            error: () => this.sucursales.set([])
        });
        this.cargar();
        this.cargarDelivery();
    }

    ngOnDestroy(): void {
        this.clickListener?.remove();
        this.circulos.forEach((c) => c.setMap(null));
        this.circulos.clear();
    }

    zonasDe(sucursalUuid: string): ZonaEntregaApi[] {
        if (sucursalUuid === SIN_TIENDA) {
            return this.zonas().filter((z) => !z.establecimientoId);
        }
        return this.zonas().filter((z) => z.establecimientoId === sucursalUuid);
    }

    sinTienda(): SucursalApi {
        return { uuid: SIN_TIENDA, nombre: 'Sin tienda asignada', direccion: null, latitud: null, longitud: null };
    }

    onMapReady() {
        const map = this.mapa()?.googleMap;
        if (!map) {
            return;
        }
        if (!this.clickListener) {
            this.clickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
                if (!e.latLng || this.seleccionada()) {
                    return;
                }
                if (!this.formVisible()) {
                    this.formVisible.set(true);
                }
                this.centroActual.set({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                this.pintarBorrador();
            });
        }
        this.dibujar();
        setTimeout(() => this.dibujar(), 400);
    }

    elegirSucursal(s: SucursalApi) {
        this.sucursalSel.set(s);
        this.cancelarEdicion();
        if (s.latitud != null && s.longitud != null) {
            this.center.set({ lat: s.latitud, lng: s.longitud });
        } else if (!this.tallerSinPunto()) {
            this.center.set(this.center());
        }
    }

    volver() {
        this.cancelarEdicion();
        this.sucursalSel.set(null);
    }

    nuevaZona() {
        this.cancelarEdicion();
        this.formVisible.set(true);
    }

    pintarBorrador() {
        const map = this.mapa()?.googleMap;
        if (!map) {
            return;
        }
        const centro = this.centroActual();
        if (this.borrador) {
            this.borrador.setMap(null);
            this.borrador = null;
        }
        if (!centro || this.seleccionada() || !this.formVisible()) {
            return;
        }
        this.borrador = new google.maps.Circle({
            center: centro,
            radius: Math.max(50, this.radio() || DEFAULT_RADIO),
            editable: false,
            draggable: false,
            fillColor: this.color(),
            fillOpacity: 0.25,
            strokeColor: this.color(),
            strokeWeight: 2,
            map
        });
    }

    seleccionar(uuid: string) {
        if (this.seleccionada() === uuid) {
            this.cancelarEdicion();
            return;
        }
        const zona = this.zonas().find((z) => z.uuid === uuid);
        if (!zona) {
            return;
        }
        this.formVisible.set(true);
        this.seleccionada.set(uuid);
        this.establecimientoDestino.set(zona.establecimientoId ?? null);
        this.nombre.set(zona.nombre);
        this.color.set(zona.color || DEFAULT_COLOR);
        this.deliveryServicioUuid.set(zona.deliveryServicioUuid ?? null);
        this.radio.set(zona.radioMetros);
        this.centroActual.set(zona.centro);
        this.confirmarEliminar.set(null);
        if (this.borrador) {
            this.borrador.setMap(null);
            this.borrador = null;
        }
        this.circulos.forEach((circle, key) => {
            const activo = key === uuid;
            circle.setEditable(activo);
            circle.setDraggable(activo);
            circle.setOptions({ strokeWeight: activo ? 4 : 2, zIndex: activo ? 10 : 1, fillOpacity: activo ? 0.35 : 0.2 });
        });
        const circle = this.circulos.get(uuid);
        const bounds = circle?.getBounds();
        if (bounds) {
            this.mapa()?.googleMap?.fitBounds(bounds, 80);
        } else {
            this.mapa()?.googleMap?.panTo(zona.centro);
        }
    }

    cancelarEdicion() {
        this.seleccionada.set(null);
        this.formVisible.set(false);
        this.nombre.set('');
        this.color.set(DEFAULT_COLOR);
        this.radio.set(DEFAULT_RADIO);
        this.deliveryServicioUuid.set(null);
        this.centroActual.set(null);
        this.confirmarEliminar.set(null);
        if (this.borrador) {
            this.borrador.setMap(null);
            this.borrador = null;
        }
        this.circulos.forEach((circle) => {
            circle.setEditable(false);
            circle.setDraggable(false);
            circle.setOptions({ strokeWeight: 2, zIndex: 1, fillOpacity: 0.25 });
        });
        this.pines.forEach((pin) => pin.setZIndex(2));
        this.dibujar();
    }

    guardar() {
        if (!this.nombre().trim()) {
            this.alerts.error('Ponle un nombre a la zona.');
            return;
        }
        const forma = this.leerForma();
        if (!forma) {
            this.alerts.error('Toca el mapa para marcar el centro del círculo.');
            return;
        }
        const body = {
            nombre: this.nombre().trim(),
            color: this.color(),
            centro: forma.centro,
            radioMetros: forma.radio,
            establecimientoId: this.sucursalSel()?.uuid === SIN_TIENDA ? this.establecimientoDestino() : (this.sucursalSel()?.uuid ?? null),
            deliveryServicioUuid: this.deliveryServicioUuid()
        };
        this.guardando.set(true);
        const req$ = this.seleccionada()
            ? this.zonasService.updateZona(this.seleccionada()!, body)
            : this.zonasService.createZona(body);
        req$.subscribe({
            next: (tx) => {
                this.guardando.set(false);
                this.alerts.ok(tx.message);
                this.cancelarEdicion();
                this.cargar();
            },
            error: (err) => {
                this.guardando.set(false);
                this.alerts.error(err?.message || 'No se pudo guardar.');
            }
        });
    }

    eliminar() {
        const uuid = this.seleccionada();
        if (!uuid) {
            return;
        }
        this.confirmarEliminar.set(null);
        this.zonasService.deleteZona(uuid).subscribe({
            next: (tx) => {
                this.alerts.ok(tx.message);
                this.cancelarEdicion();
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo eliminar.')
        });
    }

    editarDelivery(d: DeliveryServicioApi) {
        this.mostrarServicios.set(true);
        this.deliveryEditUuid.set(d.uuid);
        this.deliveryNombre.set(d.nombre);
        this.deliveryVarianteId.set(d.productoVarianteId);
        this.confirmarEliminarDelivery.set(null);
    }

    cancelarDelivery() {
        this.deliveryEditUuid.set(null);
        this.deliveryNombre.set('');
        this.deliveryVarianteId.set('');
        this.filtroVariante.set('');
        this.confirmarEliminarDelivery.set(null);
    }

    guardarDelivery() {
        if (!this.deliveryNombre().trim() || !this.deliveryVarianteId()) {
            this.alerts.error('Completa el nombre y el servicio de inventa.');
            return;
        }
        const body = { nombre: this.deliveryNombre().trim(), productoVarianteId: this.deliveryVarianteId() };
        this.guardandoDelivery.set(true);
        const req$ = this.deliveryEditUuid()
            ? this.deliveryService.update(this.deliveryEditUuid()!, body)
            : this.deliveryService.create(body);
        req$.subscribe({
            next: (tx) => {
                this.guardandoDelivery.set(false);
                this.alerts.ok(tx.message);
                this.cancelarDelivery();
                this.cargarDelivery();
            },
            error: (err) => {
                this.guardandoDelivery.set(false);
                this.alerts.error(err?.message || 'No se pudo guardar el servicio.');
            }
        });
    }

    eliminarDelivery() {
        const uuid = this.deliveryEditUuid();
        if (!uuid) {
            return;
        }
        this.confirmarEliminarDelivery.set(null);
        this.deliveryService.delete(uuid).subscribe({
            next: (tx) => {
                this.alerts.ok(tx.message);
                this.cancelarDelivery();
                this.cargarDelivery();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo eliminar el servicio.')
        });
    }

    private leerForma(): { centro: ZonaCentroApi; radio: number } | null {
        const uuid = this.seleccionada();
        if (uuid && this.circulos.has(uuid)) {
            const circle = this.circulos.get(uuid)!;
            const center = circle.getCenter();
            const radius = Math.round(circle.getRadius());
            if (center && radius > 0) {
                return { centro: { lat: center.lat(), lng: center.lng() }, radio: radius };
            }
        }
        const centro = this.centroActual();
        const radio = Math.round(this.radio() || 0);
        if (!centro || radio <= 0) {
            return null;
        }
        return { centro, radio };
    }

    private cargar() {
        this.cargando.set(true);
        this.zonasService.listZonas().subscribe({
            next: (rows) => {
                this.zonas.set(rows);
                this.cargando.set(false);
                this.dibujar();
            },
            error: (err) => {
                this.cargando.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar las zonas.');
            }
        });
    }

    private cargarDelivery() {
        this.deliveryService.list().subscribe({
            next: (rows) => this.deliveryServicios.set(rows),
            error: () => this.deliveryServicios.set([])
        });
        this.deliveryService.listVariantes().subscribe({
            next: (rows) => this.variantesServicio.set(rows),
            error: () => this.variantesServicio.set([])
        });
    }

    private dibujar() {
        const map = this.mapa()?.googleMap;
        if (!map) {
            return;
        }
        const sucursal = this.sucursalSel();
        this.circulos.forEach((c) => c.setMap(null));
        this.circulos.clear();
        this.pines.forEach((m) => m.setMap(null));
        this.pines.clear();
        for (const zona of this.zonas()) {
            if (!sucursal || !this.pertenece(zona, sucursal.uuid) || !zona.centro || !(zona.radioMetros > 0)) {
                continue;
            }
            const activo = this.seleccionada() === zona.uuid;
            const circle = new google.maps.Circle({
                center: zona.centro,
                radius: zona.radioMetros,
                editable: activo,
                draggable: activo,
                fillColor: zona.color || DEFAULT_COLOR,
                fillOpacity: activo ? 0.35 : 0.25,
                strokeColor: zona.color || DEFAULT_COLOR,
                strokeWeight: activo ? 4 : 2,
                zIndex: activo ? 10 : 1,
                map
            });
            const uuid = zona.uuid;
            circle.addListener('click', () => this.seleccionar(uuid));
            circle.addListener('radius_changed', () => this.sincronizarSeleccion(uuid));
            circle.addListener('center_changed', () => this.sincronizarSeleccion(uuid));
            this.circulos.set(uuid, circle);

            const pin = new google.maps.Marker({
                position: zona.centro,
                map,
                title: zona.nombre,
                zIndex: activo ? 11 : 2
            });
            pin.addListener('click', () => this.seleccionar(uuid));
            this.pines.set(uuid, pin);
        }
        this.encuadrar();
    }

    private encuadrar() {
        const map = this.mapa()?.googleMap;
        if (!map) {
            return;
        }
        if (this.seleccionada()) {
            return;
        }
        const bounds = new google.maps.LatLngBounds();
        let hay = false;
        this.circulos.forEach((circle) => {
            const circleBounds = circle.getBounds();
            if (circleBounds) {
                bounds.union(circleBounds);
                hay = true;
            }
        });
        if (hay) {
            map.fitBounds(bounds, 70);
        } else {
            map.setCenter(this.center());
            map.setZoom(13);
        }
    }

    private pertenece(zona: ZonaEntregaApi, sucursalUuid: string): boolean {
        return sucursalUuid === SIN_TIENDA ? !zona.establecimientoId : zona.establecimientoId === sucursalUuid;
    }

    private sincronizarSeleccion(uuid: string) {
        if (this.seleccionada() !== uuid) {
            return;
        }
        const circle = this.circulos.get(uuid);
        if (!circle) {
            return;
        }
        const center = circle.getCenter();
        if (center) {
            this.centroActual.set({ lat: center.lat(), lng: center.lng() });
        }
        this.radio.set(Math.round(circle.getRadius()));
    }
}
